import { Interval } from '@elderbyte/ngx-starter'
import { ControlTerminalMode } from '../../terminals/control-terminal-mode'
import { Instant } from '@js-joda/core'
import { TypedReference } from '../../common/typed-reference'
import { MexusEntity } from '../../mexus-entity'

export enum OutputReportType {
  WORK_ITEM_LABEL = 'WORK_ITEM_LABEL',

  WORK_ITEM_PIECE_LABEL = 'WORK_ITEM_PIECE_LABEL',

  PACKAGE_LABEL = 'PACKAGE_LABEL',

  WORK_BATCH_LABELS = 'WORK_BATCH_LABELS',

  PRODUCTION_LIST_SINGLE = 'PRODUCTION_LIST_SINGLE',

  PRODUCTION_LIST_COMBINED = 'PRODUCTION_LIST_COMBINED',

  WORK_INSTRUCTION = 'WORK_INSTRUCTION',
}

export class ReportSpec {
  public static workBatchLabels(workBatchId: string, workCenterId: string): ReportSpec {
    return new ReportSpec(
      OutputReportType.WORK_BATCH_LABELS,
      workBatchId,
      workCenterId,
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      new TypedReference(workBatchId, MexusEntity.WORK_BATCH),
      null,
      null
    )
  }

  public static itemLabel(workItemId: string): ReportSpec {
    return ReportSpec.itemLabels([workItemId])
  }

  public static itemLabels(workItemIds: string[]): ReportSpec {
    return this.workItemLabels(workItemIds, false)
  }

  public static itemPieceLabel(workItemId: string): ReportSpec {
    return ReportSpec.itemPieceLabels([workItemId])
  }

  public static itemPieceLabels(workItemIds: string[]): ReportSpec {
    return this.workItemLabels(workItemIds, true)
  }

  private static workItemLabels(workItemIds: string[], pieceLabel: boolean) {
    return new ReportSpec(
      pieceLabel ? OutputReportType.WORK_ITEM_PIECE_LABEL : OutputReportType.WORK_ITEM_LABEL,
      null,
      null,
      null,
      workItemIds,
      null,
      null,
      null,
      null,
      null,
      null,
      workItemIds?.length === 1 ? new TypedReference(workItemIds[0], MexusEntity.WORK_ITEM) : null,
      null,
      null
    )
  }

  public static itemLabelsForBatch(workBatchId: string): ReportSpec {
    return this.workItemLabelsForBatch(workBatchId, false)
  }

  public static itemPieceLabelsForBatch(workBatchId: string): ReportSpec {
    return this.workItemLabelsForBatch(workBatchId, true)
  }

  private static workItemLabelsForBatch(workBatchId: string, pieceLabel: boolean) {
    return new ReportSpec(
      pieceLabel ? OutputReportType.WORK_ITEM_PIECE_LABEL : OutputReportType.WORK_ITEM_LABEL,
      workBatchId,
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      new TypedReference(workBatchId, MexusEntity.WORK_BATCH),
      null,
      null
    )
  }

  public static packageLabels(...packageIds: string[]): ReportSpec {
    return new ReportSpec(
      OutputReportType.PACKAGE_LABEL,
      null,
      null,
      null,
      null,
      packageIds,
      null,
      null,
      null,
      null,
      null,
      packageIds?.length === 1 ? new TypedReference(packageIds[0], MexusEntity.PACKAGE) : null,
      null,
      null
    )
  }

  public static packageLabelsForBatch(workBatchId: string): ReportSpec {
    return new ReportSpec(
      OutputReportType.PACKAGE_LABEL,
      workBatchId,
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      new TypedReference(workBatchId, MexusEntity.WORK_BATCH),
      null,
      null
    )
  }

  public static prodListReportSingle(workItemIds: string[], prodListConfId: string): ReportSpec {
    return new ReportSpec(
      OutputReportType.PRODUCTION_LIST_SINGLE,
      null,
      null,
      null,
      workItemIds,
      null,
      null,
      null,
      null,
      null,
      prodListConfId,
      workItemIds?.length === 1 ? new TypedReference(workItemIds[0], MexusEntity.PACKAGE) : null,
      null,
      null
    )
  }

  public static prodListReportForBatch(workBatchId: string, prodListConfId: string): ReportSpec {
    return new ReportSpec(
      OutputReportType.PRODUCTION_LIST_SINGLE,
      workBatchId,
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      prodListConfId,
      new TypedReference(workBatchId, MexusEntity.WORK_BATCH),
      null,
      null
    )
  }

  public static prodListReportCombined(
    workCenterId: string,
    workStationId: string,
    scheduleInterval: Interval,
    timestamp: Instant,
    mode: ControlTerminalMode,
    configurationId: string
  ): ReportSpec {
    return new ReportSpec(
      OutputReportType.PRODUCTION_LIST_COMBINED,
      null,
      workCenterId,
      workStationId,
      null,
      null,
      null,
      scheduleInterval,
      timestamp,
      mode,
      configurationId,
      new TypedReference(workCenterId, MexusEntity.WORK_CENTER),
      null,
      null
    )
  }

  public static workInstructionReport(
    itemId: string,
    wcId: string,
    instructionName: string,
    action: string
  ) {
    return this.workInstructionReports([itemId], wcId, instructionName, [action])
  }

  public static workInstructionReports(
    itemIds: string[],
    wcId: string,
    instructionName: string,
    actions: string[]
  ) {
    return new ReportSpec(
      OutputReportType.WORK_INSTRUCTION,
      null,
      wcId,
      null,
      itemIds,
      null,
      actions,
      null,
      null,
      null,
      null,
      itemIds?.length === 1 ? new TypedReference(itemIds[0], MexusEntity.WORK_ITEM) : null,
      instructionName,
      null
    )
  }

  constructor(
    public reportType: OutputReportType,
    public workBatchId: string,
    public workCenterId: string,
    public workStationId: string,
    public workItemIds: string[],
    public packageIds: string[],
    public actions: string[],
    public scheduleInterval: Interval,
    public timestamp: Instant,
    public mode: ControlTerminalMode,
    public productionListConfId: string,
    public reference: TypedReference,
    public instructionName: string,
    public packageLabelData: PackageLabelData
  ) {}
}

export enum OutputActionType {
  UPLOAD = 'UPLOAD',

  PRINT = 'PRINT',

  PREVIEW = 'PREVIEW',
}

export class PrintOptions {
  public static printer(printerId: string): PrintOptions {
    return new PrintOptions(printerId)
  }

  constructor(public printerId: string) {}
}

export enum UploadMethod {
  PUT = 'PUT',

  POST = 'POST',
}

export class UploadOptions {
  public method: UploadMethod
  public url: string
}

export class OutputAction {
  public static upload(options: UploadOptions): OutputAction {
    return new OutputAction(OutputActionType.UPLOAD, null, options)
  }

  public static print(options: PrintOptions): OutputAction {
    return new OutputAction(OutputActionType.PRINT, options, null)
  }

  public static preview(): OutputAction {
    return new OutputAction(OutputActionType.PREVIEW, null, null)
  }

  constructor(
    public type: OutputActionType,
    public printOptions: PrintOptions,
    public uploadOptions: UploadOptions
  ) {}
}

export class NewReportJob {
  public static preview(reportSpec: ReportSpec): NewReportJob {
    return new NewReportJob(reportSpec, OutputAction.preview())
  }

  public static print(reportSpec: ReportSpec, options: PrintOptions): NewReportJob {
    return new NewReportJob(reportSpec, OutputAction.print(options))
  }

  public static upload(reportSpec: ReportSpec, options: UploadOptions): NewReportJob {
    return new NewReportJob(reportSpec, OutputAction.upload(options))
  }

  constructor(
    public reportSpec: ReportSpec,
    public outputAction: OutputAction
  ) {}
}

export interface PackageLabelData {
  excludeContentLabels: boolean
}
